import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { getAudioContext, createStereoNoiseBuffer } from '../audio/audio-engine';
import type { RollStartEvent, RollingEvent, RollDirectionChangeEvent, RollEndEvent } from '../../src/types';

// Chord sets to cycle through on direction changes
const CHORDS = [
  [130.81, 164.81, 196.00],  // C3 E3 G3
  [146.83, 185.00, 220.00],  // D3 F#3 A3
  [174.61, 220.00, 261.63],  // F3 A3 C4
  [196.00, 246.94, 293.66],  // G3 B3 D4
];

interface TextureEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  oscs: OscillatorNode[];
  filters: BiquadFilterNode[];
  noiseSrc: AudioBufferSourceNode | null;
  noiseGain: GainNode | null;
  noiseFilter: BiquadFilterNode | null;
  lfo: OscillatorNode | null;
  lfoGain: GainNode | null;
  chordIndex: number;
  active: boolean;
  octaveShift: number;
  speedMul: number;
}

function createTextureEngine(): TextureEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0;
  masterGain.connect(ctx.destination);

  return {
    ctx, masterGain,
    oscs: [], filters: [],
    noiseSrc: null, noiseGain: null, noiseFilter: null,
    lfo: null, lfoGain: null,
    chordIndex: 0, active: false,
    octaveShift: 0, speedMul: 1,
  };
}

function startTexture(eng: TextureEngine) {
  if (eng.active) return;
  eng.active = true;

  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;
  const chord = CHORDS[eng.chordIndex % CHORDS.length];

  // Create detuned oscillator layers
  const octMul = Math.pow(2, eng.octaveShift);
  eng.oscs = [];
  eng.filters = [];
  chord.forEach((baseFreq) => {
    const freq = baseFreq * octMul;
    [-7, 0, 7].forEach((detune) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.detune.value = detune;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 600;
      filter.Q.value = 1;

      const g = ctx.createGain();
      g.gain.value = 0.06;

      osc.connect(filter).connect(g).connect(masterGain);
      osc.start();
      eng.oscs.push(osc);
      eng.filters.push(filter);
    });
  });

  // Noise layer
  const noiseBuffer = createStereoNoiseBuffer(ctx, 2);
  eng.noiseSrc = ctx.createBufferSource();
  eng.noiseSrc.buffer = noiseBuffer;
  eng.noiseSrc.loop = true;
  eng.noiseFilter = ctx.createBiquadFilter();
  eng.noiseFilter.type = 'bandpass';
  eng.noiseFilter.frequency.value = 400;
  eng.noiseFilter.Q.value = 0.5;
  eng.noiseGain = ctx.createGain();
  eng.noiseGain.gain.value = 0.03;
  eng.noiseSrc.connect(eng.noiseFilter).connect(eng.noiseGain).connect(masterGain);
  eng.noiseSrc.start();

  // LFO tremolo — gentle pulsing driven by roll speed
  eng.lfo = ctx.createOscillator();
  eng.lfo.type = 'sine';
  eng.lfo.frequency.value = 0.4; // slow default
  eng.lfoGain = ctx.createGain();
  eng.lfoGain.gain.value = 0.06; // subtle depth
  eng.lfo.connect(eng.lfoGain).connect(masterGain.gain);
  eng.lfo.start();

  // Fade in
  masterGain.gain.setTargetAtTime(0.6, t, 0.3);
}

function stopTexture(eng: TextureEngine) {
  if (!eng.active) return;
  eng.active = false;

  const t = eng.ctx.currentTime;
  eng.masterGain.gain.setTargetAtTime(0, t, 0.4);

  // Clean up after fade
  const oscs = eng.oscs;
  const noiseSrc = eng.noiseSrc;
  const lfo = eng.lfo;
  setTimeout(() => {
    oscs.forEach((o) => { try { o.stop(); } catch {} });
    try { noiseSrc?.stop(); } catch {}
    try { lfo?.stop(); } catch {}
  }, 2000);

  eng.oscs = [];
  eng.filters = [];
  eng.noiseSrc = null;
  eng.noiseGain = null;
  eng.noiseFilter = null;
  eng.lfo = null;
  eng.lfoGain = null;
}

function changeChord(eng: TextureEngine) {
  eng.chordIndex++;
  if (!eng.active) return;

  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;
  const chord = CHORDS[eng.chordIndex % CHORDS.length];

  // Re-tune existing oscillators
  const octMul = Math.pow(2, eng.octaveShift);
  let i = 0;
  chord.forEach((baseFreq) => {
    const freq = baseFreq * octMul;
    [-7, 0, 7].forEach((detune) => {
      if (eng.oscs[i]) {
        eng.oscs[i].frequency.setTargetAtTime(freq, t, 0.2);
        eng.oscs[i].detune.setTargetAtTime(detune, t, 0.1);
      }
      i++;
    });
  });
}

function updateTimbre(eng: TextureEngine, orientX: number, orientY: number, orientZ: number, spin: number, freq: number) {
  if (!eng.active) return;
  const t = eng.ctx.currentTime;

  // X = slight pitch shift via detune
  const detuneOffset = ((orientX - 64) / 64) * 30;
  eng.oscs.forEach((osc, i) => {
    const baseDetune = [-7, 0, 7][i % 3];
    osc.detune.setTargetAtTime(baseDetune + detuneOffset, t, 0.1);
  });

  // Y = brightness (filter cutoff)
  const cutoff = 300 + (orientY / 127) * 3000;
  eng.filters.forEach((f) => f.frequency.setTargetAtTime(cutoff, t, 0.1));

  // Z = density (noise amount)
  if (eng.noiseGain) {
    const noiseVol = (orientZ / 127) * 0.08;
    eng.noiseGain.gain.setTargetAtTime(noiseVol, t, 0.1);
  }

  // Spin = stereo width / chorus (widen detune)
  const spread = 5 + (spin / 127) * 25;
  eng.oscs.forEach((osc, i) => {
    const base = [-1, 0, 1][i % 3] * spread;
    osc.detune.setTargetAtTime(base + detuneOffset, t, 0.1);
  });

  // Oscillation frequency = noise filter cutoff (faster rolling = more shimmer)
  if (eng.noiseFilter) {
    const noiseCutoff = 400 + freq * 300;
    eng.noiseFilter.frequency.setTargetAtTime(noiseCutoff, t, 0.1);
  }

  // Speed → LFO tremolo rate (0.3–2.5 Hz) and depth (0.04–0.12)
  // Keeps the texture mellow: slow rolls barely pulse, fast rolls breathe more
  // Global speed modifier scales the LFO rate, clamped to stay mellow
  if (eng.lfo) {
    const baseRate = 0.3 + Math.min(freq, 3) * 0.73;
    const lfoRate = Math.min(4, baseRate * eng.speedMul);
    eng.lfo.frequency.setTargetAtTime(lfoRate, t, 0.2);
  }
  if (eng.lfoGain) {
    const lfoDepth = 0.04 + Math.min(freq, 3) * 0.027;
    eng.lfoGain.gain.setTargetAtTime(lfoDepth, t, 0.2);
  }
}

export function RollTexture() {
  const { rollDetector, connected, speedModifier, octaveOffset } = useOddball();
  const engineRef = useRef<TextureEngine | null>(null);
  const [rolling, setRolling] = useState(false);
  const [heading, setHeading] = useState('');
  const [hue, setHue] = useState(200);
  const [freq, setFreq] = useState(0);
  const speedRef = useRef(1.0);
  const octaveRef = useRef(0);
  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => {
    if (!connected) {
      const eng = engineRef.current;
      if (eng) stopTexture(eng);
      setRolling(false);
    }
  }, [connected]);

  useEffect(() => {
    const eng = createTextureEngine();
    engineRef.current = eng;

    const syncModifiers = () => {
      eng.octaveShift = octaveRef.current;
      eng.speedMul = speedRef.current;
    };

    const onRollStart = (_evt: RollStartEvent) => {
      syncModifiers();
      startTexture(eng);
      setRolling(true);
    };

    const onRolling = (e: RollingEvent) => {
      syncModifiers();
      updateTimbre(eng, e.orientation.x, e.orientation.y, e.orientation.z, e.spinSpeed, e.oscillationFreq);
      setHeading(e.heading);
      setHue((e.orientation.x / 127) * 360);
      setFreq(e.oscillationFreq);
    };

    const onDirectionChange = (_evt: RollDirectionChangeEvent) => {
      changeChord(eng);
    };

    const onRollEnd = (_evt: RollEndEvent) => {
      stopTexture(eng);
      setRolling(false);
    };

    rollDetector.on('rollStart', onRollStart);
    rollDetector.on('rolling', onRolling);
    rollDetector.on('rollDirectionChange', onDirectionChange);
    rollDetector.on('rollEnd', onRollEnd);

    // If already rolling when we mount, start immediately
    if (rollDetector.rolling) {
      startTexture(eng);
      setRolling(true);
    }

    return () => {
      rollDetector.off('rollStart', onRollStart);
      rollDetector.off('rolling', onRolling);
      rollDetector.off('rollDirectionChange', onDirectionChange);
      rollDetector.off('rollEnd', onRollEnd);
      stopTexture(eng);
      eng.masterGain.disconnect();
    };
  }, [rollDetector]);

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        background: rolling
          ? `radial-gradient(ellipse at center, hsla(${hue}, 30%, 20%, 0.6), transparent)`
          : 'transparent',
        borderRadius: '12px',
        padding: '40px',
        transition: 'background 0.5s',
      }}
    >
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Roll Texture</h2>

      <div
        style={{
          fontSize: '1.6rem',
          fontWeight: 300,
          color: rolling ? `hsl(${hue}, 50%, 65%)` : '#333',
          letterSpacing: '0.1em',
          transition: 'color 0.3s',
        }}
      >
        {rolling ? 'Rolling...' : 'Waiting for roll'}
      </div>

      {rolling && heading && (
        <div style={{ fontSize: '0.85rem', color: '#666', fontFamily: 'monospace' }}>
          heading: {heading} &middot; freq: {freq.toFixed(1)} Hz
        </div>
      )}

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Roll = textured pad &middot; Orientation = timbre control &middot; Direction change = chord shift
      </div>
    </div>
  );
}
